// Users service - moved from database.js (includes user settings)
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  setDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp, 
  db 
} from '../../utils/database/firebase-config';
import { 
  updatePassword,
  deleteUser as deleteAuthUser
} from 'firebase/auth';
import { auth } from '../../utils/database/firebase-config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auditService, AUDIT_MODULES, AUDIT_ACTIONS, AUDIT_TAGS } from './audit';

const userLabel = (u) => u?.name || u?.displayName || u?.email || u?.id || 'user';

export const userSettingsService = {
  async getUserSettings(userId) {
    const settingsRef = doc(db, 'userSettings', userId);
    const snapshot = await getDoc(settingsRef);
    if (snapshot.exists()) return { id: snapshot.id, ...snapshot.data() };
    return null;
  },
  async updateUserSettings(userId, settingsData) {
    const settingsRef = doc(db, 'userSettings', userId);
    await setDoc(settingsRef, { ...settingsData, updatedAt: serverTimestamp() }, { merge: true });
    return true;
  },
  async updateUserProfile(userId, profileData) {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { ...profileData, updatedAt: serverTimestamp() });
    return true;
  }
};

export const usersService = {
  async getUsers(companyId, options = {}) {
    try {
      const usersRef = collection(db, 'users');
      let q = query(usersRef, where('companyId', '==', companyId));
      
      // Add ordering
      if (options.orderBy) {
        q = query(q, orderBy(options.orderBy, options.orderDirection || 'asc'));
      } else {
        q = query(q, orderBy('createdAt', 'desc'));
      }
      
      // Add limit if specified
      if (options.limit) {
        q = query(q, limit(options.limit));
      }
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting users:', error);
      throw error;
    }
  },

  async getUser(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const snapshot = await getDoc(userRef);
      if (snapshot.exists()) return { id: snapshot.id, ...snapshot.data() };
      return null;
    } catch (error) {
      console.error('Error getting user:', error);
      throw error;
    }
  },

  async createUser(userData) {
    try {
      console.log('Creating user with data:', userData);
      
      // Use Cloud Function to create user without affecting current session
      const functions = getFunctions();
      const createUserAccount = httpsCallable(functions, 'createUserAccount');
      
      const result = await createUserAccount({
        userData: {
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          password: userData.password,
          role: userData.role,
          status: userData.status || 'active',
          permissions: userData.permissions || {},
          companyId: userData.companyId
        }
      });
      
      console.log('User created successfully via Cloud Function:', result.data);
      auditService.log({
        module: AUDIT_MODULES.USER,
        action: AUDIT_ACTIONS.CREATE,
        recordId: result.data.userId,
        recordLabel: userLabel(userData),
        after: { ...userData, password: undefined },
        tags: [AUDIT_TAGS.ALL, AUDIT_TAGS.USER_ACCESS],
        companyId: userData.companyId,
        meta: { role: userData.role, email: userData.email }
      });
      return result.data.userId;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },

  async updateUser(userId, userData) {
    try {
      const userRef = doc(db, 'users', userId);
      let before = null;
      try {
        const snap = await getDoc(userRef);
        if (snap.exists()) before = { id: snap.id, ...snap.data() };
      } catch { /* best effort */ }

      const updateData = {
        ...userData,
        updatedAt: serverTimestamp()
      };

      // Remove password from update data if present
      delete updateData.password;

      await updateDoc(userRef, updateData);

      auditService.logUpdate({
        module: AUDIT_MODULES.USER,
        recordId: userId,
        recordLabel: userLabel({ ...before, ...updateData }),
        before,
        after: { ...(before || {}), ...updateData },
        extraTags: [AUDIT_TAGS.USER_ACCESS],
        meta: { email: before?.email || userData.email }
      });
      return true;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },

  async deleteUser(userId) {
    try {
      // Delete user document from Firestore
      const userRef = doc(db, 'users', userId);
      let before = null;
      try {
        const snap = await getDoc(userRef);
        if (snap.exists()) before = { id: snap.id, ...snap.data() };
      } catch { /* best effort */ }

      await deleteDoc(userRef);

      auditService.log({
        module: AUDIT_MODULES.USER,
        action: AUDIT_ACTIONS.DELETE,
        recordId: userId,
        recordLabel: userLabel(before),
        before,
        after: null,
        tags: [AUDIT_TAGS.ALL, AUDIT_TAGS.USER_ACCESS, AUDIT_TAGS.DELETIONS],
        companyId: before?.companyId,
        meta: { email: before?.email, role: before?.role }
      });

      // Note: Deleting Firebase Auth user requires admin SDK on backend
      // For now, we'll just deactivate the user
      return true;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  },

  async deactivateUser(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { 
        status: 'inactive',
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error deactivating user:', error);
      throw error;
    }
  },

  async activateUser(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { 
        status: 'active',
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error activating user:', error);
      throw error;
    }
  },

  async updateUserLastLogin(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const currentData = userDoc.data();
        await updateDoc(userRef, {
          lastLogin: serverTimestamp(),
          loginCount: (currentData.loginCount || 0) + 1,
          updatedAt: serverTimestamp()
        });
      }
      return true;
    } catch (error) {
      console.error('Error updating user last login:', error);
      throw error;
    }
  },

  async getUserStats(companyId) {
    try {
      const users = await this.getUsers(companyId);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      
      console.log('🔍 Debug - getUserStats:', {
        totalUsers: users.length,
        today: today.toISOString(),
        usersWithLastLogin: users.filter(u => u.lastLogin).length
      });
      
      const activeToday = users.filter(u => {
        if (!u.lastLogin) {
          console.log(`🔍 User ${u.email} has no lastLogin`);
          return false;
        }
        
        let lastLogin;
        try {
          lastLogin = u.lastLogin.toDate ? u.lastLogin.toDate() : new Date(u.lastLogin);
        } catch (error) {
          console.log(`🔍 Error parsing lastLogin for ${u.email}:`, error);
          return false;
        }
        
        const isActiveToday = lastLogin >= today;
        console.log(`🔍 User ${u.email} lastLogin: ${lastLogin.toISOString()}, isActiveToday: ${isActiveToday}`);
        return isActiveToday;
      }).length;
      
      return {
        total: users.length,
        active: users.filter(u => u.status === 'active').length,
        inactive: users.filter(u => u.status === 'inactive').length,
        admins: users.filter(u => u.role === 'admin').length,
        operations: users.filter(u => u.role === 'operations').length,
        accounting: users.filter(u => u.role === 'accounting').length,
        clients: users.filter(u => u.role === 'client').length,
        activeToday: activeToday,
        newThisWeek: users.filter(u => {
          if (!u.createdAt) return false;
          const createdAt = u.createdAt.toDate ? u.createdAt.toDate() : new Date(u.createdAt);
          return createdAt >= weekAgo;
        }).length
      };
    } catch (error) {
      console.error('Error getting user stats:', error);
      throw error;
    }
  },

  async checkEmailExists(email, excludeUserId = null) {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email.toLowerCase()));
      const snapshot = await getDocs(q);
      
      if (excludeUserId) {
        return snapshot.docs.some(doc => doc.id !== excludeUserId);
      }
      
      return !snapshot.empty;
    } catch (error) {
      console.error('Error checking email exists:', error);
      throw error;
    }
  },
  // Add missing functions for user preferences
  async getUserPreferences(userId) {
    const settingsRef = doc(db, 'userSettings', userId);
    const snapshot = await getDoc(settingsRef);
    if (snapshot.exists()) {
      const data = snapshot.data();
      return {
        language: data.language || 'ar',
        theme: data.theme || 'light',
        notifications: data.notifications || true,
        ...data
      };
    }
    // Return default preferences if no settings exist
    return {
      language: 'ar',
      theme: 'light',
      notifications: true
    };
  },
  async updateUserLanguage(userId, language) {
    const settingsRef = doc(db, 'userSettings', userId);
    await setDoc(settingsRef, { 
      language, 
      updatedAt: serverTimestamp() 
    }, { merge: true });
    return true;
  }
}; 